import { useMemo, useState, useCallback, useRef } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import { CHART, METRIC_COLORS } from "../lib/designTokens";
import { downsampleChartData } from "../lib/laps";
import { canonicalActivitySport } from "../lib/sportAnalyticsGate";
import { distanceUnit, distanceValue, fmtDistance, fmtDuration } from "../lib/format";
import {
  analyticsRateLabel,
  analyticsRateUnit,
  enrichChartDataWithRate,
  formatRateLiveDisplay,
  getRateChartDataKey,
  paceYAxisDomain,
  streamRateChartStats,
  usesPaceAnalytics,
} from "../lib/analyticsRate";
import { formatDistanceTick } from "../lib/chartAxis";
import { metricStats } from "../lib/metricStats";

const STRIP_H = 56;
const ELEV_H = 64;
const LEFT_W = 104;
const RIGHT_W = 80;
const X_AXIS_H = 20;

function formatLive(value, decimals = 0) {
  if (value == null || Number.isNaN(value)) return null;
  return decimals ? Number(value).toFixed(decimals) : String(Math.round(value));
}

function nearestPoint(data, km) {
  if (!data?.length || km == null) return null;
  let best = data[0];
  let bestDiff = Math.abs(best.km - km);
  for (let i = 1; i < data.length; i++) {
    const diff = Math.abs(data[i].km - km);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = data[i];
    }
  }
  return best;
}

const MetricSidebar = ({ label, color, stats, testId, showOverall = false }) => {
  const hasStats =
    stats?.max != null || stats?.avg != null || (showOverall && stats?.overall != null);

  return (
    <div
      className="shrink-0 px-2 flex flex-col justify-center border-r border-dark-border/60"
      style={{ width: LEFT_W }}
      data-testid={testId}
    >
      <div className="text-xs font-heading uppercase tracking-wide leading-none" style={{ color }}>
        {label}
      </div>
      {hasStats && (
        <div className="mt-1 font-mono text-[10px] leading-snug text-dark-text-muted">
          {stats?.max != null && (
            <div>
              Max <span className="text-dark-text-secondary" data-testid={testId ? `${testId}-max` : undefined}>{stats.max}</span>
            </div>
          )}
          {stats?.avg != null && (
            <div>
              Avg <span className="text-dark-text-secondary" data-testid={testId ? `${testId}-avg` : undefined}>{stats.avg}</span>
            </div>
          )}
          {showOverall && stats?.overall != null && (
            <div>
              Overall{" "}
              <span className="text-dark-text-secondary" data-testid={testId ? `${testId}-overall` : undefined}>
                {stats.overall}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MetricValue = ({ liveValue, fallbackDisplay, unit, color, formatDisplay, decimals = 0 }) => {
  const isLive = liveValue != null && !Number.isNaN(liveValue);
  const display = isLive
    ? formatDisplay?.(liveValue) ?? formatLive(liveValue, decimals)
    : fallbackDisplay ?? "—";

  return (
    <div
      className="shrink-0 flex flex-col items-end justify-center pr-3 border-l border-dark-border/40"
      style={{ width: RIGHT_W }}
    >
      <div
        className={`font-mono leading-none tabular-nums ${isLive ? "text-xl font-bold" : "text-base font-semibold text-dark-text-muted"}`}
        style={isLive ? { color } : undefined}
      >
        {display ?? "—"}
      </div>
      <div className="text-[10px] text-dark-text-muted font-mono mt-1 uppercase">{unit}</div>
    </div>
  );
};

const ChartStrip = ({
  data,
  dataKey,
  color,
  type = "line",
  stats,
  showXAxis,
  hoverKm,
  maxKm,
  unitSystem = "metric",
  yDomain = ["auto", "auto"],
}) => (
  <div className="flex-1 min-w-0 h-full">
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: showXAxis ? 2 : -8 }}>
        <CartesianGrid stroke={CHART.grid} strokeDasharray="3 3" vertical={false} />
        {showXAxis ? (
          <XAxis
            dataKey="km"
            type="number"
            domain={["dataMin", "dataMax"]}
            stroke={CHART.tick}
            tickFormatter={(v) => formatDistanceTick(v, maxKm, unitSystem)}
            tick={{ fontSize: 10, fill: CHART.tick, fontFamily: "JetBrains Mono" }}
            tickLine={false}
            axisLine={{ stroke: CHART.axis }}
            tickCount={6}
            allowDecimals={false}
            height={X_AXIS_H}
          />
        ) : (
          <XAxis dataKey="km" hide />
        )}
        <YAxis hide domain={yDomain} />
        {hoverKm != null && (
          <ReferenceLine x={hoverKm} stroke={CHART.hoverLine} strokeOpacity={0.5} strokeWidth={1} />
        )}
        {stats?.avgNumeric != null && (
          <ReferenceLine
            y={stats.avgNumeric}
            stroke={color}
            strokeDasharray="3 3"
            strokeOpacity={0.3}
          />
        )}
        {type === "area" ? (
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            fill={color}
            fillOpacity={0.18}
            strokeWidth={1.5}
            dot={false}
            connectNulls
            isAnimationActive={false}
          />
        ) : (
          <Line
            type="linear"
            dataKey={dataKey}
            stroke={color}
            strokeWidth={1.5}
            dot={false}
            connectNulls
            isAnimationActive={false}
            activeDot={{ r: 4, strokeWidth: 0, fill: color }}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  </div>
);

const MetricRow = ({
  label,
  color,
  stats,
  unit,
  dataKey,
  decimals,
  data,
  hover,
  showXAxis,
  type = "line",
  maxKm,
  unitSystem = "metric",
  sidebarTestId,
  showOverall = false,
  formatLiveValue,
  yDomain = ["auto", "auto"],
}) => (
  <div className="flex w-full border-t border-dark-border/50" style={{ height: STRIP_H }}>
    <MetricSidebar
      label={label}
      color={color}
      stats={stats}
      testId={sidebarTestId}
      showOverall={showOverall}
    />
    <ChartStrip
      data={data}
      dataKey={dataKey}
      color={color}
      type={type}
      stats={stats}
      showXAxis={showXAxis}
      hoverKm={hover?.km}
      maxKm={maxKm}
      unitSystem={unitSystem}
      yDomain={yDomain}
    />
    <MetricValue
      liveValue={hover ? hover[dataKey] : null}
      fallbackDisplay={stats?.avg != null ? String(stats.avg) : null}
      unit={unit}
      color={color}
      formatDisplay={formatLiveValue}
      decimals={decimals}
    />
  </div>
);

const LapStatsBar = ({ lap, sport, unitSystem }) => (
  <div className="px-4 py-2 border-b border-dark-border flex flex-wrap gap-x-4 gap-y-1 text-[10px] font-mono text-dark-text-secondary">
    <span className="text-dark-text-primary">Lap {lap.lapNumber}</span>
    <span>Dist <span className="text-dark-text-primary">{fmtDistance(lap.distance_m, unitSystem)}</span></span>
    {lap.elevation_m != null && (
      <span>
        Grad{" "}
        <span className="text-dark-text-primary">
          {lap.elevation_m > 0 ? "+" : ""}{lap.elevation_m}m
        </span>
      </span>
    )}
    <span>Time <span className="text-dark-text-primary">{fmtDuration(lap.duration_seconds)}</span></span>
    {lap.normalized_power != null && (
      <span>NP <span style={{ color: METRIC_COLORS.normalizedPower }}>{lap.normalized_power}W</span></span>
    )}
    {lap.tss != null && (
      <span>TSS <span style={{ color: METRIC_COLORS.tss }}>{lap.tss}</span></span>
    )}
  </div>
);

export const LapProfileChart = ({
  data,
  lapStats,
  sport,
  hasPower,
  hasHr,
  hasCadence,
  hasSpeed,
  hasTemperature = false,
  totalDistanceKm,
  rangeSubtitle = null,
  title = "Ride Data",
  testId,
  unitSystem = "metric",
  analyticsSidebarStats = null,
}) => {
  const [hover, setHover] = useState(null);
  const scrubRef = useRef(null);

  const isLapView = Boolean(lapStats);
  const paceMode = usesPaceAnalytics(sport);
  const rateDataKey = getRateChartDataKey(sport);
  const chartData = useMemo(() => {
    const built = data;
    const scoped = isLapView ? built : downsampleChartData(built, 250);
    return enrichChartDataWithRate(scoped, sport, unitSystem);
  }, [data, isLapView, sport, unitSystem]);

  const elevStats = useMemo(() => metricStats(chartData, "elevation", 0), [chartData]);
  const streamRateStats = useMemo(
    () => streamRateChartStats(chartData, sport, unitSystem),
    [chartData, sport, unitSystem]
  );
  const powerStats = useMemo(() => metricStats(chartData, "power", 0), [chartData]);
  const hrStats = useMemo(() => metricStats(chartData, "hr", 0), [chartData]);
  const streamCadenceStats = useMemo(() => metricStats(chartData, "cadence", 0), [chartData]);
  const tempStats = useMemo(() => metricStats(chartData, "temperature", 0), [chartData]);

  const speedStats = useMemo(() => {
    if (!analyticsSidebarStats?.speed) return streamRateStats;
    return {
      max: analyticsSidebarStats.speed.max ?? streamRateStats.max,
      avg: analyticsSidebarStats.speed.avg ?? streamRateStats.avg,
      maxNumeric: streamRateStats.maxNumeric,
      avgNumeric: streamRateStats.avgNumeric,
    };
  }, [analyticsSidebarStats, streamRateStats]);

  const cadenceStats = useMemo(() => {
    if (!analyticsSidebarStats?.cadence) return streamCadenceStats;
    return {
      max: analyticsSidebarStats.cadence.max ?? streamCadenceStats.max,
      avg: analyticsSidebarStats.cadence.avg ?? streamCadenceStats.avg,
      overall: analyticsSidebarStats.cadence.overall ?? null,
    };
  }, [analyticsSidebarStats, streamCadenceStats]);

  const isSegmentMode = analyticsSidebarStats?.mode === "segment";
  const rateLabel = analyticsRateLabel(sport, isSegmentMode);
  const cadenceSport = canonicalActivitySport(sport);
  const cadenceLabel = cadenceSport === "running"
    ? (isSegmentMode ? "Running cadence · Seg" : "Running cadence")
    : (isSegmentMode ? "Cadence · Seg" : "Cadence");
  const cadenceUnit = cadenceSport === "running" ? "spm" : "rpm";
  const formatRateLive = useCallback(
    (value) => formatRateLiveDisplay(value, sport),
    [sport]
  );
  const rateYDomain = useMemo(
    () => (paceMode ? paceYAxisDomain(chartData, rateDataKey) : ["auto", "auto"]),
    [paceMode, chartData, rateDataKey]
  );

  const rows = useMemo(() => {
    const list = [
      {
        key: "elevation",
        label: "Elevation",
        color: "#8E8E93",
        unit: "m",
        stats: elevStats,
        decimals: 0,
        type: "area",
      },
    ];
    if (hasSpeed) {
      list.push({
        key: rateDataKey,
        label: rateLabel,
        color: "#32ADE6",
        unit: analyticsRateUnit(sport, unitSystem),
        stats: speedStats,
        type: "line",
        sidebarTestId: "analytics-speed-sidebar",
        formatLiveValue: formatRateLive,
        yDomain: rateYDomain,
      });
    }
    if (hasPower) {
      list.push({
        key: "power",
        label: "Power",
        color: METRIC_COLORS.power,
        unit: "W",
        stats: powerStats,
        decimals: 0,
        type: "line",
      });
    }
    if (hasHr) {
      list.push({
        key: "hr",
        label: "Heart Rate",
        color: METRIC_COLORS.hr,
        unit: "bpm",
        stats: hrStats,
        decimals: 0,
        type: "line",
      });
    }
    if (hasCadence) {
      list.push({
        key: "cadence",
        label: cadenceLabel,
        color: "#AF52DE",
        unit: cadenceUnit,
        stats: cadenceStats,
        decimals: 0,
        type: "line",
        sidebarTestId: "analytics-cadence-sidebar",
        showOverall: !isSegmentMode && cadenceStats.overall != null,
      });
    }
    if (hasTemperature) {
      list.push({
        key: "temperature",
        label: "Temperature",
        color: "#AC8E68",
        unit: "°C",
        stats: tempStats,
        decimals: 0,
        type: "line",
      });
    }
    return list;
  }, [
    sport,
    unitSystem,
    hasSpeed,
    hasPower,
    hasHr,
    hasCadence,
    hasTemperature,
    elevStats,
    speedStats,
    powerStats,
    hrStats,
    cadenceStats,
    tempStats,
    rateLabel,
    cadenceLabel,
    cadenceUnit,
    isSegmentMode,
    rateDataKey,
    formatRateLive,
    rateYDomain,
  ]);

  const lastRowKey = rows[rows.length - 1]?.key;
  const minKm = chartData[0]?.km ?? 0;
  const maxKm = chartData[chartData.length - 1]?.km ?? 0;
  const formatKm = (v) => formatDistanceTick(v, maxKm, unitSystem);
  const distanceLabel = distanceUnit(unitSystem);

  const handleScrubMove = useCallback(
    (e) => {
      if (!scrubRef.current || !chartData.length) return;
      const rect = scrubRef.current.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const km = minKm + ratio * (maxKm - minKm);
      setHover(nearestPoint(chartData, km));
    },
    [chartData, minKm, maxKm]
  );

  const handleScrubLeave = useCallback(() => setHover(null), []);

  const subtitle = isLapView
    ? `${distanceValue(lapStats.distance_m || 0, unitSystem).toFixed(2)} ${distanceLabel} lap`
    : rangeSubtitle
      ? rangeSubtitle
      : totalDistanceKm
        ? `${distanceValue(totalDistanceKm, unitSystem).toFixed(1)} ${distanceLabel} · Full ride`
        : "Full ride";

  return (
    <div className="app-card overflow-hidden w-full" data-testid={testId}>
      <div className="px-5 py-4 border-b border-[var(--border-primary)] flex items-baseline justify-between gap-3">
        <div>
          <div className="card-label">Profile</div>
          <h3 className="section-title mt-0.5">
            {isLapView ? `Lap ${lapStats.lapNumber}` : title}
          </h3>
        </div>
        <div className="text-right">
          <div className="meta-text">{subtitle}</div>
          {hover && (
            <div className="text-sm font-mono text-dark-text-primary mt-1 font-semibold">
              {formatKm(hover.km)} {distanceLabel}
            </div>
          )}
        </div>
      </div>

      {isLapView && <LapStatsBar lap={lapStats} sport={sport} unitSystem={unitSystem} />}

      {/* One shared hover zone — all strips move together */}
      <div
        ref={scrubRef}
        className="relative pb-0.5 select-none"
        onMouseMove={handleScrubMove}
        onMouseLeave={handleScrubLeave}
        style={{ height: ELEV_H + (rows.length - 1) * STRIP_H + (lastRowKey ? X_AXIS_H : 0) }}
      >
        <div className="flex w-full" style={{ height: ELEV_H }}>
          <MetricSidebar label="Elevation" color="#8E8E93" stats={elevStats} />
          <ChartStrip
            data={chartData}
            dataKey="elevation"
            color="#8E8E93"
            type="area"
            stats={elevStats}
            showXAxis={lastRowKey === "elevation"}
            hoverKm={hover?.km}
            maxKm={maxKm}
            unitSystem={unitSystem}
          />
          <MetricValue
            liveValue={hover?.elevation}
            fallbackDisplay={
              elevStats?.avg != null ? formatLive(elevStats.avg, 0) : null
            }
            unit="m"
            color="#8E8E93"
          />
        </div>

        {rows.slice(1).map((row) => (
          <MetricRow
            key={row.key}
            label={row.label}
            color={row.color}
            stats={row.stats}
            unit={row.unit}
            dataKey={row.key}
            decimals={row.decimals}
            data={chartData}
            hover={hover}
            showXAxis={lastRowKey === row.key}
            type={row.type}
            maxKm={maxKm}
            unitSystem={unitSystem}
            sidebarTestId={row.sidebarTestId}
            showOverall={row.showOverall}
            formatLiveValue={row.formatLiveValue}
            yDomain={row.yDomain}
          />
        ))}
      </div>
    </div>
  );
};
